import React, { useState } from "react";
import { PX_PER_METER } from "../../../shared/rating/stats-format";
import styles from "./ResultBoxes.module.scss";
import SectionTabs from "../../../shared/section-tabs/SectionTabs";

// "Match stats" box on the Full Results screen: the 8 fun-stat tiles from the
// winner-screens prototype, fed by core's match-stats aggregate. Shows one
// player at a time; when several of the viewer's players are in the match
// (local multiplayer / two seats on one computer), name pills switch between
// them. Tiles with no data (older server payloads) are simply hidden.

export interface StatsPlayer {
  id: string;
  name: string;
}

interface Props {
  // core aggregateMatchStats shape: { totalRoundTime, totalRounds, byPlayer }
  stats: any;
  players: StatsPlayer[]; // the switchable players (the viewer's own seats)
  initialPlayerId?: string;
}

function formatDistance(px: number): React.ReactNode {
  const meters = px / PX_PER_METER;
  if (meters >= 1000) {
    return (
      <>
        {(meters / 1000).toFixed(1)} <small>km</small>
      </>
    );
  }
  return (
    <>
      {Math.round(meters)} <small>m</small>
    </>
  );
}

const ICONS: Record<string, React.ReactNode> = {
  powerUps: (
    <svg viewBox="0 0 16 16"><path d="M9 1 3 9h3.5L5.8 15 13 6.5H9.3L11 1Z" fill="currentColor" /></svg>
  ),
  distance: (
    <svg viewBox="0 0 16 16"><path d="M1 8c1.2-4 2.8-4 4 0s2.8 4 4 0 2.8-4 4 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
  ),
  turns: (
    <svg viewBox="0 0 16 16">
      <path d="M12.5 14V8a4 4 0 0 0-4-4H4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 1.5 4.5 4l3 2.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  survival: (
    <svg viewBox="0 0 16 16">
      <circle cx="8" cy="8.5" r="5.8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 5.2v3.3l2.4 1.6M6.5 1h3" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  crashes: (
    <svg viewBox="0 0 16 16">
      <path d="M8 1.2A5.4 5.4 0 0 0 2.6 6.6c0 2 .9 3.4 2.2 4.2v2.6h6.4v-2.6c1.3-.8 2.2-2.2 2.2-4.2A5.4 5.4 0 0 0 8 1.2Z" fill="currentColor" />
      <circle cx="5.9" cy="6.9" r="1.35" fill="#0b101d" />
      <circle cx="10.1" cy="6.9" r="1.35" fill="#0b101d" />
      <path d="M6.8 11.2v2.2M9.2 11.2v2.2" stroke="#0b101d" strokeWidth="1.1" />
    </svg>
  ),
  gaps: (
    <svg viewBox="0 0 16 16">
      <circle cx="8" cy="8" r="5.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="8" cy="8" r="2.3" fill="currentColor" />
    </svg>
  ),
  rounds: (
    <svg viewBox="0 0 16 16"><path d="M8 1.6l2 4 4.4.6-3.2 3.1.8 4.3L8 11.5l-4 2.1.8-4.3L1.6 6.2 6 5.6z" fill="currentColor" /></svg>
  ),
  heart: (
    <svg viewBox="0 0 16 16"><path d="M8 13.8C2.8 10.3 1.1 7.4 2.6 5.2c1.3-1.9 4-1.6 5.4.6 1.4-2.2 4.1-2.5 5.4-.6 1.5 2.2-.2 5.1-5.4 8.6Z" fill="currentColor" /></svg>
  ),
};

function Tile({ color, icon, label, children }: { color: string; icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className={styles.fstat} style={{ "--fc": color } as React.CSSProperties}>
      <span className={styles.fsIco}>{icon}</span>
      <span className={styles.fsCol}>
        <span className={styles.fsLabel}>{label}</span>
        <span className={styles.fsValue}>{children}</span>
      </span>
    </div>
  );
}

export default function MatchStatsBox({ stats, players, initialPlayerId }: Props) {
  const withStats = players.filter((p) => stats && stats.byPlayer && stats.byPlayer[p.id]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId && withStats.some((p) => p.id === selectedId)
    ? selectedId
    : (initialPlayerId && withStats.some((p) => p.id === initialPlayerId) ? initialPlayerId : withStats[0]?.id);
  if (!activeId) return null;
  const s = stats.byPlayer[activeId];
  const pu = s.powerUps || {};
  const survivalPct = stats.totalRoundTime > 0 ? Math.round((s.aliveTime / stats.totalRoundTime) * 100) : null;

  // No box or heading of its own: the tiles stand on the page beside the rating card.
  return (
    <div className={styles.statsBare}>
      {withStats.length > 1 ? (
        // Couch play: one tab per local player, in the app's tab band.
        <div className={styles.statsTabs}>
          <SectionTabs tabs={withStats.map((p) => ({ id: p.id, label: p.name }))} value={activeId} onChange={setSelectedId} rewindOnChange={false} />
        </div>
      ) : null}
      <div className={styles.fstats}>
        <Tile color="#7CFC66" icon={ICONS.powerUps} label="Power-ups grabbed">
          <i className={`${styles.pu} ${styles.puG}`} />{pu.self || 0}&nbsp;&nbsp;
          <i className={`${styles.pu} ${styles.puR}`} />{pu.others || 0}&nbsp;&nbsp;
          <i className={`${styles.pu} ${styles.puB}`} />{pu.all || 0}
        </Tile>
        <Tile color="#28c4d4" icon={ICONS.distance} label="Distance wormed">
          {formatDistance(s.distance || 0)}
        </Tile>
        <Tile color="#ffcf4d" icon={ICONS.turns} label="Turns taken">
          {s.turnsLeft || 0} <small>L</small>&nbsp;&nbsp;&nbsp;{s.turnsRight || 0} <small>R</small>
        </Tile>
        <Tile color="#4fd9c4" icon={ICONS.survival} label="Longest survival">
          {Math.round(s.longestSurvival || 0)}<small>s</small>{" "}
          {s.longestSurvivalRound ? <small>· round {s.longestSurvivalRound}</small> : null}
        </Tile>
        <Tile color="#ff5c74" icon={ICONS.crashes} label="Crashes caused">
          {s.crashesCaused || 0} <small>worms downed</small>
        </Tile>
        <Tile color="#bf7bfb" icon={ICONS.gaps} label="Gaps threaded">
          {s.gapsThreaded || 0} <small>close squeezes</small>
        </Tile>
        <Tile color="#ffe066" icon={ICONS.rounds} label="Rounds won">
          {s.roundsWon || 0} <small>of {stats.totalRounds}</small>
        </Tile>
        {survivalPct !== null ? (
          <Tile color="#ff7ab8" icon={ICONS.heart} label="Survival rate">
            {survivalPct}<small>%</small> <small>of round time</small>
          </Tile>
        ) : null}
      </div>
    </div>
  );
}
